import React, { useState, useEffect } from 'react';
import './TutorialModal.css';

interface AIPlotTutorialModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AIPlotTutorialModal: React.FC<AIPlotTutorialModalProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const steps = [
    {
      title: 'Step 1: Generate Plot with AI',
      description: 'Start by clicking the "Story Plot Planning" button. Enter your story idea and click "🤖 Generate All Plots" to create characters, backgrounds, and page content automatically. Watch the progress bar as AI crafts your story!',
      images: ['/screenshots/plot-tutorial/ai-tutorial1-1.png', '/screenshots/plot-tutorial/ai-tutorial1-2.png']
    },
    {
      title: 'Step 2: Select Font Settings',
      description: 'Click the "Font Settings" button to choose your preferred font style. Select the font family, size, weight, and colors that match your story\'s mood and audience.',
      images: ['/screenshots/plot-tutorial/ai-tutorial2-1.png', '/screenshots/plot-tutorial/ai-tutorial2-2.png']
    },
    {
      title: 'Step 3: Review & Edit Content',
      description: 'Review the generated pages in the plot modal. You can edit background selections, scene prompts, and text content directly. Update character dialogue, narration, and descriptions to perfect your story.',
      images: ['/screenshots/plot-tutorial/ai-tutorial3-1.png', '/screenshots/plot-tutorial/ai-tutorial3-2.png']
    },
    {
      title: 'Step 4: Generate Pages with AI',
      description: 'Click "AI Generate Page" next to each page to automatically create the complete page with images and text. The AI will compose scenes with your characters and backgrounds, then analyze the scene to position text optimally. Check the token estimate before generating!',
      images: ['/screenshots/plot-tutorial/ai-tutorial4-1.png', '/screenshots/plot-tutorial/ai-tutorial4-2.png']
    },
    {
      title: 'Step 5: Refine & Repeat',
      description: 'After generation, review the page and make any final adjustments to text positioning, colors, or content. Update elements as needed, then move to the next page. Repeat the process for all pages in your story!',
      images: ['/screenshots/plot-tutorial/ai-tutorial5-1.png', '/screenshots/plot-tutorial/ai-tutorial5-2.png']
    }
  ];

  // Auto-cycle images every 2 seconds
  useEffect(() => {
    if (!isOpen) return;
    
    const currentStepImages = steps[currentStep].images;
    if (currentStepImages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % currentStepImages.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [currentStep, isOpen]);

  // Reset image index when step changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [currentStep]);

  if (!isOpen) return null;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    onClose();
  };

  const handleSkip = () => {
    handleClose();
  };

  return (
    <div className="tutorial-overlay" onClick={handleClose}>
      <div className="tutorial-modal" onClick={(e) => e.stopPropagation()}>
        <button className="tutorial-close" onClick={handleClose}>×</button>
        
        <div className="tutorial-header">
          <h2>🤖 AI-Powered Story Creation</h2>
          <p className="tutorial-subtitle">Learn how to create complete storybooks with AI assistance</p>
        </div>

        <div className="tutorial-content">
          <div className="tutorial-step-indicator">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`tutorial-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                onClick={() => setCurrentStep(index)}
              />
            ))}
          </div>

          <div className="tutorial-step">
            <h3>{steps[currentStep].title}</h3>
            <div className="tutorial-image-container">
              <img 
                src={steps[currentStep].images[currentImageIndex]} 
                alt={steps[currentStep].title}
                className="tutorial-image"
              />
              {steps[currentStep].images.length > 1 && (
                <div className="tutorial-image-dots">
                  {steps[currentStep].images.map((_, idx) => (
                    <span 
                      key={idx} 
                      className={`image-dot ${idx === currentImageIndex ? 'active' : ''}`}
                    />
                  ))}
                </div>
              )}
            </div>
            <p className="tutorial-description">{steps[currentStep].description}</p>
          </div>
        </div>

        <div className="tutorial-footer">
          <button className="tutorial-btn tutorial-btn-secondary" onClick={handleSkip}>
            Skip AI Tutorial
          </button>
          
          <div className="tutorial-navigation">
            {currentStep > 0 && (
              <button className="tutorial-btn tutorial-btn-outline" onClick={handlePrevious}>
                ← Previous
              </button>
            )}
            <button className="tutorial-btn tutorial-btn-primary" onClick={handleNext}>
              {currentStep < steps.length - 1 ? 'Next →' : 'Continue to Manual Tools Tutorial'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIPlotTutorialModal;
